/**
 * Date coercion and the member-facing date phrasing that goes with it.
 *
 * Extracted from `memberEngagement.ts` (and one copy that had drifted into
 * `MemberHomePage`) so the dashboard utils can reuse the phrasing without
 * importing a 671-line module built around a different set of card builders.
 *
 * The strings here are member-facing and pinned by `dateLabels.test.ts` — they
 * read the same on Home, on the plan page, and in the next-step hero, which is
 * the whole reason they live in one place.
 *
 * Every function takes an optional `now` so the phrasing is testable without
 * mocking the clock. Callers should keep omitting it.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function coerceDate(value?: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'object' && value && 'toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const converted = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  const converted = new Date(value as string | number);
  return Number.isNaN(converted.getTime()) ? null : converted;
}

export function formatDateLabel(value?: unknown): string {
  const date = coerceDate(value);
  if (!date) return 'Date pending';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/** Whole days from `from` to `to`, counting calendar days rather than elapsed hours. */
export function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);
}

/**
 * "Overdue by 3 days" / "Due today" / "Due tomorrow" / "Due in 5 days" /
 * "Next due 14 Aug 2026". Anything undated is "Schedule to be confirmed" —
 * never a fabricated date.
 */
export function formatDueLabel(value?: string | Date | null, now: Date = new Date()): string {
  const dueDate = coerceDate(value);
  if (!dueDate) return 'Schedule to be confirmed';

  const diffDays = daysBetween(now, dueDate);
  if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`;
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays <= 7) return `Due in ${diffDays} days`;
  return `Next due ${formatDateLabel(dueDate)}`;
}

/**
 * "Just now" / "6h ago" / "3d ago" / "14 Aug" — for a last-sync or last-seen
 * timestamp, where recency matters more than the exact date.
 */
export function formatRelativeLabel(
  value?: unknown,
  now: Date = new Date(),
  emptyLabel = 'No sync yet',
): string {
  const date = coerceDate(value);
  if (!date) return emptyLabel;

  const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
